import {
  generateUUID,
  deepClone,
  toKebab,
  EventBus,
  EventBusListeners,
  extractAllNumbers,
  maxDotNumber,
  reactive,
  watchEffect,
  parseNumber,
  shallowClone,
  newConstructor
  // saveReactiveState,
  // restoreReactiveState,
  // getReactiveState
} from "@zyzgroup/core-common";
import { distance, angle, polar, toDegree, toRadian } from "./math";
import { TSVGParsedPathData } from "../svg/DOMSVGUtil";
import { YZCanvasContext } from "../canvas/YZCanvasContext";
import { PathD } from "./line/";
import { Matrix } from "./Matrix";
import { Timeline, TweenRawOptions, TweenValue } from "../animate";

export type TShapeOptions = {
  d?: string | TSVGParsedPathData[] | PathD;
  originMatrix?: Matrix;

  independentTransformInGroup?: boolean;

  /// 坐标系原点
  /// options 中为相对于画布 (0,0) 点的绝对值
  /// this    中为相对于自身 (0,0) 点的相对值
  centerX?: number;
  centerY?: number;

  // translate
  x?: number;
  y?: number;

  // rotate
  degree?: number;

  // scale
  scalar?: number;
  scalarX?: number;
  scalarY?: number;

  diffXInGroup?: number;
  diffYInGroup?: number;
  diffDegreeInGroup?: number;
  diffScalarXInGroup?: number;
  diffScalarYInGroup?: number;

  // 速度
  vx?: number;
  vy?: number;

  // 加速度
  ax?: number;
  ay?: number;

  // 重量
  mass?: number;

  /// 样式属性
  attrs?: Record<string, string | CanvasGradient>;

  /// 是否绘制
  isShow?: boolean;

  /// 是否可拖拽
  enableDrag?: boolean;
  alongPath?: PathD;

  /// 是否鼠标放开后运动到target
  targetToAfterDragFree?: { targetX: number; targetY: number; easing?: number };

  /// 是否跟随mouse移动而移动
  followMouse?: boolean;

  /// 是否绘制行军蚁边框
  strokeArmyAnts?: boolean;
};

export interface ShapeEventBusListeners extends EventBusListeners {
  draging(dx: number, dy: number, excludeShapeInGroup?: Shape): void;
  clicked(shape: Shape): void;
}

export type TShapeRunOptions = {
  gravity?: number; // 重力常量，例如 0.1
  friction?: number; // 摩擦系数常量，例如 1
};
export type TShapeRunEasingOptions = TShapeRunOptions & {
  easing?: number; // 例如 0.05
};
export type TShapeRunSpringOptions = TShapeRunOptions & {
  spring?: number; // 例如 0.03
};

export class Shape extends EventBus<ShapeEventBusListeners> {
  // [index: string | number | symbol]: any;

  public id: string;
  public originD?: string | TSVGParsedPathData[] | PathD;
  public originMatrix?: Matrix;

  public independentTransformInGroup: boolean;

  public forceCXY = false;

  /// 相对于自身 (0,0) 点的相对值
  public cX: number;
  public cY: number;

  public x: number;
  public y: number;

  public degree: number;

  public scalarX: number;
  public scalarY: number;

  public diffXInGroup: number;
  public diffYInGroup: number;
  public diffDegreeInGroup: number;
  public diffScalarXInGroup: number;
  public diffScalarYInGroup: number;

  public vx: number;
  public vy: number;
  public ax: number;
  public ay: number;
  public mass: number;

  private attrs: Record<string, string | CanvasGradient>;

  public enableDrag: boolean;
  public alongPath?: PathD;

  public isDragging = false;
  public isMouseIn = false;

  public targetToAfterDragFree?: {
    targetX: number;
    targetY: number;
    easing?: number;
  };

  public followMouse: boolean;

  public strokeArmyAnts: boolean;
  public strokeArmyAntsOffset = 0;
  public strokeDashTotalLength = 0;

  public isShow = true;
  public el?: HTMLElement | SVGElement;

  public pathd?: PathD;
  public usePathDtoCanvasContext = false;

  public parent?: Shape;
  public shapes: Shape[] = [];

  constructor(options?: TShapeOptions) {
    super();

    this.id = generateUUID();
    if (options?.d) {
      this.originD = options?.d;
    }
    if (options?.originMatrix) {
      this.originMatrix = options.originMatrix;
    }

    this.independentTransformInGroup =
      typeof options?.independentTransformInGroup == "boolean"
        ? options.independentTransformInGroup
        : false;

    this.x = options?.x || 0;
    this.y = options?.y || 0;

    this.cX = 0;
    this.cY = 0;
    if (
      typeof options?.centerX == "number" &&
      typeof options?.centerY == "number"
    ) {
      this.forceSetCXY(options.centerX - this.x, options.centerY - this.y);
    }

    this.degree = options?.degree || 0;

    this.scalarX = 1;
    this.scalarY = 1;
    if (options?.scalar !== void 0) {
      this.scalarX = options.scalar;
      this.scalarY = options.scalar;
    }
    if (options?.scalarX !== void 0) {
      this.scalarX = options.scalarX;
    }
    if (options?.scalarY !== void 0) {
      this.scalarY = options.scalarY;
    }

    this.diffXInGroup = options?.diffXInGroup || 0;
    this.diffYInGroup = options?.diffYInGroup || 0;
    this.diffDegreeInGroup = options?.diffDegreeInGroup || 0;
    this.diffScalarXInGroup = options?.diffScalarXInGroup || 1;
    this.diffScalarYInGroup = options?.diffScalarYInGroup || 1;

    this.vx = options?.vx || 0;
    this.vy = options?.vy || 0;
    this.ax = options?.ax || 0;
    this.ay = options?.ay || 0;
    this.mass = options?.mass || 1;
    this.attrs = {
      // "paint-order": "markers stroke fill", // 先 stroke 再 fill
      // "paint-order": "fill stroke markers", // default
      stroke: "none",
      fill: "none"
      // "pointerEvents": "none",
    };
    if (options?.attrs) {
      this.setAttr(options?.attrs);
    }
    this.enableDrag = options?.enableDrag || false;
    this.alongPath = options?.alongPath;
    this.targetToAfterDragFree = options?.targetToAfterDragFree;
    this.followMouse = options?.followMouse || false;
    this.isShow = typeof options?.isShow == "boolean" ? options?.isShow : true;
    this.strokeArmyAnts = options?.strokeArmyAnts || false;

    if (this.strokeArmyAnts) {
      const sourceDashArray = this.getAttr("stroke-dasharray", "4 4");
      this.setAttr("stroke-dasharray", sourceDashArray);
      this.strokeDashTotalLength = extractAllNumbers(
        sourceDashArray as string
      ).reduce((acc, d) => acc + d, 0);
    }

    /// 父类调用时，子类 this.toSVGPathData() 有些值可能还未初始化
    /// clone() 对象暂时无法执行下面方法！
    // nextTick(() => {
    //   this.pathd = this.initPathD();
    // });

    const proxy = reactive(this);
    return proxy;
  }

  /// 强制设置 坐标系原点
  public forceSetCXY(cX: number, cY: number) {
    this.cX = cX;
    this.cY = cY;
    this.forceCXY = true;
  }

  /// 子类可覆盖
  public shapeInit() {
    // watchEffect(() => {});
  }

  /// 统一初始化 pathd
  public initPathD(): PathD {
    if (!this.pathd) {
      /// 初始化
      this.pathd = new PathD();
      /// Shape其他属性改变，涉及到 pathd.normalizedLines 时，自动更新
      watchEffect(() => {
        this.pathd?.reset(
          this.originD || this.toSVGPathData(),
          this.originMatrix
        );
      });
      if (this.pathd && !this.forceCXY) {
        /// 改变 默认坐标系原点 为自身中心点
        const rect =
          this.shapes.length == 0
            ? this.pathd.getBoundingClientRect()
            : this.getBoundingClientRect();
        // const rect = this.getBoundingClientRect();
        if (rect) {
          this.pathd.cX = this.cX = rect.x + rect.width / 2 - this.pathd.x;
          this.pathd.cY = this.cY = rect.y + rect.height / 2 - this.pathd.y;
          // console.log("shape", rect, this.cX, this.cY);
        }
      }
      this.shapeInit();
    }
    return this.pathd;
  }

  public frameStart(mouse?: { x: number; y: number }): void {
    // this.pathd = this.initPathD();
  }

  public frameUpdate(
    mouse?: { x: number; y: number },
    ctx?: YZCanvasContext
  ): void {
    /// 同步 this.pathd 属性
    this.pathd = this.initPathD();
    this.pathd.update(this);
    /// 绘制行军蚁边框
    if (this.strokeArmyAnts) {
      if (!this.isMouseIn) {
        this.setAttr("stroke-dashoffset", String(this.strokeArmyAntsOffset++));
        if (this.strokeArmyAntsOffset > this.strokeDashTotalLength) {
          this.strokeArmyAntsOffset = 0;
        }
      }
    }
  }

  public frameEnd(mouse?: { x: number; y: number }): void {
    ///
  }

  /// Shape SVG 使用 PathD transform 系统，使用相对值
  /// 子类一定要覆盖 toSVGPathData ！
  /// 唯一例外是 group ，可以不覆盖！
  /// translate() rotate() scale() 等 transfrom 交给 PathD 处理， YZSVG 不处理
  /// 属性值自己保存
  public toSVGPathData(): TSVGParsedPathData[] {
    // return [["m", 0, 0]];
    return [];
  }

  /// 子类不要覆盖 toSVGPathD ！
  /// 供 YZSVG 调用
  public toSVGPathD(): string {
    return this.pathd?.toSVGPathD() || "";
  }

  /// Shape Canvas 使用 PathD transform 系统 或 原生canvas系统
  /// 如果子类没有覆盖 toCanvasContext , 则默认使用 PathD 的 toCanvasContext
  ///   - translate() rotate() scale() 等 transfrom 交给 PathD 处理，YZCanvas 不处理
  ///   - canvas.ctx.createLinearGradient 使用绝对值
  /// 如果子类覆盖 toCanvasContext , 则使用 原生canvas系统
  ///   - translate() rotate() scale() 等 transfrom 交给 YZCanvas 处理
  ///   - canvas.ctx.createLinearGradient 使用相对值
  /// 不包含 fill() 及 stroke()
  /// 属性值自己保存
  public toCanvasContext(
    ctx: YZCanvasContext
    // ctx: CanvasRenderingContext2D
    // | WebGLRenderingContext
    // | WebGL2RenderingContext
    // | GPUCanvasContext
    // | ImageBitmapRenderingContext
  ): void {
    this.usePathDtoCanvasContext = true;
    this.pathd?.toCanvasContext(ctx);
  }

  /// 目前 仅 Text 无法转换为 PathD, 也就无 getBoundingClientRect
  /// 子类可覆盖
  /// 绝对值
  public getBoundingClientRect(): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null {
    return this.pathd?.getBoundingClientRect() || null;
  }

  /// 绝对值
  public get center(): { x: number; y: number } {
    const rect = this.getBoundingClientRect();
    const c = { x: this.x, y: this.y };
    if (rect) {
      c.x = rect.x + rect.width / 2;
      c.y = rect.y + rect.height / 2;
    }
    return c;
  }

  public trim() {
    if (this.parent) {
      return;
    }
    const rect = this.getBoundingClientRect();
    if (!rect) {
      return;
    }
    this.shapes.forEach((s) => {
      const originMatrix = s.originMatrix
        ? s.originMatrix.clone()
        : new Matrix();
      originMatrix.translate(-rect.x + s.x, -rect.y + s.y);
      s.originMatrix = originMatrix; /// set 引发 自动 watchEffect
    });
    const originMatrix = this.originMatrix
      ? this.originMatrix.clone()
      : new Matrix();
    originMatrix.translate(-rect.x + this.x, -rect.y + this.y);
    this.originMatrix = originMatrix; /// set 引发 自动 watchEffect
  }

  public isPointInPath(
    x: number,
    y: number,
    ctx: YZCanvasContext | SVGSVGElement
  ): boolean {
    if (ctx instanceof YZCanvasContext) {
      return ctx.isPointInPath(new Path2D(this.toSVGPathD()), x, y);
    }
    /// 性能差
    let point;
    try {
      point = new SVGPoint(x, y);
    } catch (e) {
      // fallback for browsers that don't support SVGPoint as an argument
      point = ctx.createSVGPoint();
      point.x = x;
      point.y = y;
    }
    if (this.el instanceof SVGGeometryElement) {
      return this.el.isPointInFill(point);
    } else {
      const rect = this.getBoundingClientRect();
      if (rect) {
        return (
          x >= rect.x &&
          x <= rect.x + rect.width &&
          y >= rect.y &&
          y <= rect.y + rect.height
          /// UNSUCCESS
          // && PathD.interPathHelper(
          //   this.toSVGPathData(),
          //   DOMSVGUtil.normalizeSVGPath([
          //     ["M", x, y],
          //     ["H", rect.x + rect.width + 10]
          //     // ["l", rect.width + 10, Math.random()]
          //   ])
          // ).length %
          //   2 ==
          //   1
        );
      }
    }
    return false;
  }

  public isPointInStroke(
    x: number,
    y: number,
    ctx: YZCanvasContext | SVGSVGElement
  ): boolean {
    if (ctx instanceof YZCanvasContext) {
      return ctx.isPointInStroke(new Path2D(this.toSVGPathD()), x, y);
    }
    /// 性能差
    let point;
    try {
      point = new SVGPoint(x, y);
    } catch (e) {
      // fallback for browsers that don't support SVGPoint as an argument
      point = ctx.createSVGPoint();
      point.x = x;
      point.y = y;
    }
    if (this.el instanceof SVGGeometryElement) {
      return this.el.isPointInStroke(point);
    }
    return false;
    /// UNDO: node-canvas return undefined
  }

  public clone() {
    // return deepClone(this);
    return newConstructor(this).copy(this);
    // return shallowClone(this).copy(this);
  }

  public copy(shape: Shape) {
    this.id = shape.id;
    this.originD = shape.originD;
    this.originMatrix = shape.originMatrix;
    this.independentTransformInGroup = shape.independentTransformInGroup;
    this.x = shape.x;
    this.y = shape.y;
    this.cX = shape.cX;
    this.cY = shape.cY;
    this.degree = shape.degree;
    this.scalarX = shape.scalarX;
    this.scalarY = shape.scalarY;
    this.diffXInGroup = shape.diffXInGroup;
    this.diffYInGroup = shape.diffYInGroup;
    this.diffDegreeInGroup = shape.diffDegreeInGroup;
    this.diffScalarXInGroup = shape.diffScalarXInGroup;
    this.diffScalarYInGroup = shape.diffScalarYInGroup;
    this.vx = shape.vx;
    this.vy = shape.vy;
    this.ax = shape.ax;
    this.ay = shape.ay;
    this.mass = shape.mass;
    this.attrs = {
      ...shape.attrs
    };
    this.enableDrag = shape.enableDrag;
    this.alongPath = shape.alongPath;
    this.targetToAfterDragFree = shape.targetToAfterDragFree;
    this.followMouse = shape.followMouse;
    this.isShow = shape.isShow;
    this.strokeArmyAnts = shape.strokeArmyAnts;
    this.strokeDashTotalLength = shape.strokeDashTotalLength;
    return this;
  }

  public save() {
    /// toRaw Proxy
    // saveReactiveState(this.raw);
    // console.log("save", this.raw, getReactiveState(this.raw));
  }

  public restore() {
    /// toRaw Proxy
    // restoreReactiveState(this.raw);
    // console.log("restore", this.raw, getReactiveState(this.raw));
  }

  public setAttr(obj: Record<string, string | CanvasGradient>): void;
  public setAttr(name: string, value: string | CanvasGradient): void;
  public setAttr(
    nameOrObj: string | Record<string, string | CanvasGradient>,
    value?: string | CanvasGradient
  ): void {
    if (typeof nameOrObj !== "string") {
      Object.assign(
        this.attrs,
        Object.fromEntries(
          Object.entries(nameOrObj).map(([k, v]) => {
            return [toKebab(k), v];
          })
        )
      );
    } else {
      if (value) {
        this.attrs[toKebab(nameOrObj)] = value;
      }
    }
  }

  public getAttr(
    name: string,
    defaultValue: string | CanvasGradient
  ): string | CanvasGradient;
  public getAttr(name: string): string | CanvasGradient | undefined;
  public getAttr(
    name: string,
    defaultValue?: string | CanvasGradient
  ): string | CanvasGradient | undefined {
    return this.attrs[name] || this.attrs[toKebab(name)] || defaultValue;
  }

  public getAttrs(): Record<string, string | CanvasGradient> {
    return this.attrs;
  }

  public hasAttr(name: string): boolean {
    return !!this.attrs[name] || !!this.attrs[toKebab(name)];
  }

  public distance(target: { x: number; y: number }): number {
    return distance(this.x, this.y, target.x, target.y);
  }

  public rotateTo(target: { x: number; y: number }) {
    this.degree = toDegree(angle(this.x, this.y, target.x, target.y));
  }

  public followTo(target: { x: number; y: number }, length = 3) {
    this.rotateTo(target);
    const { dx: vx, dy: vy } = polar(toRadian(this.degree), length);
    this.vx = vx;
    this.vy = vy;
    this.run();
  }

  public followAccelerationTo(target: { x: number; y: number }, length = 0.05) {
    this.rotateTo(target);
    const { dx: ax, dy: ay } = polar(toRadian(this.degree), length);
    this.ax = ax;
    this.ay = ay;
    this.run();
  }

  /// 碰撞检测
  public checkCollision(target: Shape) {
    /// UNDO
  }

  /// 回弹
  public checkWall(options: {
    wall: { x: number; y: number; width: number; height: number };
    bounce?: number;
  }) {
    /// UNDO
  }

  /// 物体之间引力
  public gravitateTo(target: Shape) {
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const distSQ = dx * dx + dy * dy;
    const dist = Math.sqrt(distSQ);
    const force = (this.mass * target.mass) / distSQ;
    const ax = (force * dx) / dist;
    const ay = (force * dy) / dist;
    this.vx += ax / this.mass;
    this.vy += ay / this.mass;
    target.vx -= ax / target.mass;
    target.vy -= ay / target.mass;
  }

  public run(options?: TShapeRunOptions) {
    // 加速度
    this.vx += this.ax;
    this.vy += this.ay;

    // 重力
    const gravity = parseNumber(options?.gravity, 0);
    this.vy += gravity;

    // 摩擦力方式一
    // const friction = parseNumber(options?.friction, 0);
    // if (friction > 0) {
    //   const radian = Math.atan2(this.vy, this.vx);
    //   let speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    //   if (speed > friction) {
    //     speed -= friction;
    //   } else {
    //     speed = 0;
    //   }
    //   this.vx = Math.cos(radian) * speed;
    //   this.vy = Math.sin(radian) * speed;
    // }

    // 摩擦力方式二
    const friction = parseNumber(options?.friction, 1);
    this.vx *= friction;
    this.vy *= friction;

    this.x += this.vx;
    this.y += this.vy;
  }

  public runEaseTo(
    target: { x: number; y: number },
    options?: TShapeRunEasingOptions
  ) {
    const easing = parseNumber(options?.easing, 0.05);
    const dx = target.x - this.cX - this.x;
    const dy = target.y - this.cY - this.y;
    this.vx = dx * easing;
    this.vy = dy * easing;
    this.run(options);
  }

  public runSpringTo(
    target: { x: number; y: number } | { x: number; y: number }[],
    options?: TShapeRunSpringOptions
  ) {
    const spring = parseNumber(options?.spring, 0.03);
    (Array.isArray(target) ? target : [target]).forEach((t) => {
      const dx = t.x - this.cX - this.x;
      const dy = t.y - this.cY - this.y;
      const ax = dx * spring;
      const ay = dy * spring;
      this.vx += ax;
      this.vy += ay;
    });
    this.run({ friction: 0.9, ...options });
  }

  public springMassTo(
    target: Shape,
    options?: {
      springLength?: number;
      spring?: number;
    }
  ): number {
    const springLength = parseNumber(options?.springLength, 100);
    const spring = parseNumber(options?.spring, 0.0001);
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < springLength) {
      const ax = dx * spring;
      const ay = dy * spring;
      this.vx += ax / this.mass;
      this.vy += ay / this.mass;
      target.vx -= ax / target.mass;
      target.vy -= ay / target.mass;
      return 1 - dist / springLength; /// alpha: 0 ~ 1
    }
    return 0;
  }

  getTotalLength(): number {
    return this.initPathD().getTotalLength();
  }

  interpolate(t: number): { x: number; y: number } {
    return this.initPathD().interpolate(t);
  }

  getPointAtLength(length: number): {
    x: number;
    y: number;
    alpha: number;
  } {
    return this.initPathD().getPointAtLength(length);
  }

  public trailPath(
    trail: Shape,
    progress = 0 // 0 ~ 1
  ): {
    x: number;
    y: number;
    alpha: number;
  } {
    const totalLength = this.getTotalLength();
    trail.originD = this.initPathD().getSubpath(0, progress * totalLength);
    return this.getPointAtLength(progress * totalLength);
  }

  public strokeDashTween(frameDuration = 1000, options?: TweenRawOptions) {
    const pathd = this.initPathD();
    const totalLength = maxDotNumber(pathd.getTotalLength(), 0);
    this.setAttr({
      strokeDasharray: `${totalLength} ${totalLength}`,
      strokeDashoffset: `${totalLength}`
    });
    const prevOnUpdate = options?.onUpdate;
    const tl = new Timeline({ frameDuration });
    tl.addTween(
      { offset: totalLength },
      { offset: 0 },
      Object.assign(options || {}, {
        onUpdate: (coords: TweenValue, elapsed: number) => {
          this.setAttr("strokeDashoffset", "" + maxDotNumber(coords.offset, 2));
          prevOnUpdate?.(coords, elapsed);
        }
      })
    );
    tl.play();
  }
}

export default Shape;
